import React from 'react';
import { View, ScrollView, Text } from 'react-native';
import i18n from 'i18n-js';

import { scale } from '@components/frame';
import { localTime, isPhone } from '@helpers';
import ColorWrapper from '@styles/colors';
import { getBoxShadowStyle } from '@styles/shadow';
import Line from '@components/common/line';
import EditField from '@components/common/editField';
import LineButton from '@components/common/lineButton';
import EditableLine from '@components/common/editableLine';
import Header from '@components/settings/v2/common/SettingsHeader';

import MachineDetailsFields from './MachineDetailsField';

import Loading from '@components/loading';
import { font } from '@styles/fonts';
import ImagesContainer, { ImagesContainerMode } from '@v2/pages/common/components/ImagesContainer/ImagesContainer';
import { SCREEN_BORDER_RADIUS, SCREEN_PADDING_HORIZONTAL } from '@v2/pages/common/constants/base.constants';

import { useManageMachineDetails } from './hooks/useManageMachineDetails';

const MachineDetails = (props) => {
  const { C, navigation } = props;

  const {
    id,
    machine,
    loaded,
    machineBrand,
    machineConnection,
    machineStatus,
    fullName,
    shortName,
    isConnected,
    connectionLoading,
    removing,
    showEditField,
    editFieldConfig,
    initialSectionData,
    sectionsData,
    removeMachine,
    triggerConnection,
    triggerEditConnectionMethod,
    toggleEditField,
    backButtonAction,
  } = useManageMachineDetails({ C });

  if (!loaded) {
    return <Loading background={C('Background/Light/Detail')} />;
  }

  if (!machine.machineId) {
    return (
      <View style={{ width: '100%' }}>
        <Header backBtnText="Machines" backButtonAction={backButtonAction} name={'MachineDetails'} navigation={navigation} />
        <View style={{ width: '100%', marginTop: scale(50), justifyContent: 'center', alignContent: 'center', alignItems: 'center' }}>
          <Text style={[font.Body, { color: C('Label/Light/Primary') }]}>{i18n.t('Machine not found.')}</Text>
        </View>
      </View>
    );
  }

  if (!machine.isActive) {
    return (
      <View style={{ width: '100%' }}>
        <Header backBtnText="Machines" backButtonAction={backButtonAction} name={'MachineDetails'} navigation={navigation} />
        <View style={{ width: '100%', marginTop: scale(50), justifyContent: 'center', alignContent: 'center', alignItems: 'center' }}>
          <Text style={[font.Body, { color: C('Label/Light/Primary') }]}>
            {i18n.t('This machine was deleted on {{date}} by {{user}}.', {
              date: localTime(machine.modifiedOn).format('YYYY-MM-DD HH:mm'),
              user: machine.modifiedBy,
            })}
          </Text>
          <Text style={[font.Body, { color: C('Label/Light/Primary') }]}>{i18n.t('It may still appear in past performance charts.')}</Text>
        </View>
      </View>
    );
  }

  return [
    <EditField
      key="EditField"
      {...{
        C,
        showModal: showEditField,
        onClose: toggleEditField,
        ...editFieldConfig,
      }}
    />,
    <View key="mainMachineContent" style={{ height: '100%', width: '100%', backgroundColor: C('Background/Light/Detail'), alignItems: 'center' }}>
      <Header
        backBtnText="Machines"
        name={'MachineDetails'}
        action={triggerEditConnectionMethod}
        backButtonAction={backButtonAction}
        header={fullName}
        subHeader={{
          text: i18n.t(isConnected ? 'Connected' : 'Not Connected'),
          color: isConnected ? C('System/Light/Green') : C('Label/Light/Error'),
        }}
        navigation={navigation}
      />
      <ScrollView showsVerticalScrollIndicator={false} style={{ width: '100%', flex: 1 }} contentContainerStyle={{ paddingBottom: scale(50) }}>
        <View
          style={{
            paddingHorizontal: scale(SCREEN_PADDING_HORIZONTAL),
          }}
        >
          <View style={[getBoxShadowStyle({ type: 'Field' }, C), { width: '100%', marginTop: scale(2), borderRadius: scale(SCREEN_BORDER_RADIUS) }]}>
            <ImagesContainer
              {...{
                maxDetailsWidth: '100%',
                panelViewMode: false,
                mode: ImagesContainerMode.ROW,
                machine,
                noBorder: true,
                machineId: id,
              }}
            />
            <EditableLine
              {...{
                item: {
                  value: machineConnection.switchedOn,
                  key: 'switchedOn',
                  name: isConnected ? 'Machine Connection' : 'Try To Connect',
                  noArrow: true,
                  isLable: true,
                  oneLine: true,
                  small: false,
                  width: 170,
                  type: 'boolean',
                  loading: connectionLoading,
                  onChange: (v) => triggerConnection(v),
                },
                index: 0,
                array: [{}],
                C,
                topBorder: true,
                bottomBorder: true,
                roundTop: false,
                roundBottom: false,
                threeColumns: true,
              }}
            />
            {initialSectionData.map((item, index, array) => (
              <Line key={index} {...{ C, item, index, array, roundTop: false, roundBottom: initialSectionData.length - 1 === index }} />
            ))}
          </View>
          {sectionsData.map((data, index) => (
            <MachineDetailsFields key={index} {...{ ...data, C }} />
          ))}
          <View style={[{ width: '100%', marginTop: scale(20) }]}>
            <LineButton
              {...{
                onPress: removeMachine,
                loading: removing,
                text: 'Remove Machine',
                color: 'Chart/Light/Warning',
                C,
              }}
            />
          </View>
        </View>
      </ScrollView>
    </View>,
  ];
};

export default ColorWrapper(MachineDetails);
